import axios from "axios";
import { SITE_API_URL, SITE_CONNECTION_TIMEOUT_AVERAGE, SIGNALR_CONNECTIONID_HEADER_NAME } from "../settings";
import { setCurrentUser} from "../redux/slices/currentuserslice";
import { setGenericErrorPage } from "../redux/slices/genericerrorpageslice";
import { addNotification } from "../redux/slices/notificationsslice";
import { setAppDialog } from "../redux/slices/appdialogslice";
import { setCounter } from "../redux/slices/notificationscounterslice";
import { transformReportsMenu } from "../lib/utils";

let store = {};

export const injectStore = (_store) => store = _store;

const axiosInstance = axios.create ( {
  baseURL: SITE_API_URL,
  timeout: SITE_CONNECTION_TIMEOUT_AVERAGE,
  withCredentials: true, //required in order for the browser to automatically pass the cookies
  maxRedirects: 0,//VERY IMPORTANT BECAUSE THE SERVER USES HTTPREDIRECT MIDDLEWARE
});

axiosInstance.interceptors.request.use((config) => {
  const currentUser = store.getState().currentUser;

  //if current user is authenticated, add auth token in case cookies are disabled
  if (currentUser.IsAuthenticated && currentUser.Jwt)
     config.headers.Authorization="bearer " + currentUser.Jwt;

  //console.log("Pros.interceptors.request config: > ", currentUser.IsAuthenticated);
  //console.log(config);

  //test if no connection
  if (navigator && navigator.onLine == false)
    return Promise.reject({ __transformResponse__noConnection: true });

  return config;
});


axiosInstance.interceptors.response.use(
  (response) => {
    //I am here => 200 is returned => either Succeeded or Succeeded false such as business logic error
    //console.log("Pros.interceptors.response.success response: > ");
    //console.log(response);
    
    //for our backend, we'll always have response.data  
    if (response.data.Succeeded)
      return {
        status: response.status,
        data: response.data,
        headers: response.headers,
        succeeded: true, 
        responseReceived: true,
        unAuthenticated: false,
        unAuthorized: false,
        canceled: false,
        noConnection: false, 
        timedOut: false,
        networkError: false,
        unhandled: false
      };
    else 
    {
      return Promise.reject(
        {
          status: response.status, 
          data: response.data, 
          headers: response.headers,
          succeeded: false, 
          responseReceived: true,
          unAuthenticated: false,
          unAuthorized: false,
          canceled: false,
          noConnection: false, 
          timedOut: false, 
          networkError: false,
          unhandled: false
        });
    }    
  },
  async (err) => {
    // console.log("Pros.Interceptors.Response.Error err > ", err);
    // console.log(err);

    let errorResult = err;

    //transform err
    if (axios.isCancel(err))
      errorResult = {
        //since no response is received => i have no status nor data nor headers
        succeeded: false, 
        responseReceived: false,
        unAuthenticated: false,
        unAuthorized: false,
        canceled: true,
        noConnection: false, 
        timedOut: false,
        networkError: false, 
        unhandled: false
      };
    else if (err.response)
    {     
      if (err.response.status == 403) //handle unauthorized
        errorResult = {
          status: 403, 
          data: err.response.data,
          headers: err.response.headers,
          succeeded: false, 
          responseReceived: true,
          unAuthenticated: false,
          unAuthorized: true,
          canceled: false,
          noConnection: false, 
          timedOut: false, 
          networkError: false,
          unhandled: false,
        };
      else if (err.response.data)
      {
        let currentUser = store.getState().currentUser;

        if (err.response.status == 401 && err.response.data?.ErrorCodes?.TokenExpired)
        {
            console.log("TOKEN EXPIRED");

            //token expired
            let prevConfig = err.config;            

            //MUST MODIFY THIS CODE IN INTELLIGENCE THAT DEAL WITH SAASENABLER SO THAT THE REFRESH TOKEN IS NOT SENT IN HEADER
            //AS BELOW BECAUSE SASSENABLER WON'T SEND THE REFRESH TOKEN IN BODY ANYMORE FOR SECURITY
            //BELOW WE'LL REMOVE THE X-REFRESHTOKEN HEADER AND AXIOS WILL SEND IT FROM THE COOKIE AND SAASENABLER WILL READ IT FROM THE COOKIE
            //ON THE REFRESHTOKEN ENDPOINT
            try
            {
              const _response = await api({
                url: "auth/refreshToken", 
                method: "post",
                headers: currentUser.IsAuthenticated ? {"x-refreshtoken":  currentUser.RefreshToken} : {}
              });

              //i am here => i got it because refresh token on the server returns 200 only if success
              //dispatch to currentUser
              store.dispatch(setCurrentUser({..._response.data.Data, ReportsMenu: transformReportsMenu(_response.data.Data.ReportsMenu)}));

              store.dispatch(setCounter(_response.data.Data.UnreadNotificationsCount));

              return axiosInstance(prevConfig);
            }
            catch(ex)
            {
              //failed to refresh the token
              return Promise.reject(ex);
            }
        }
        else if (err.response.status == 401)
        {
            //not authenticated => dispatch if user is authenticated in order to update the store
            if (currentUser.IsAuthenticated)
                store.dispatch(setCurrentUser({IsAuthenticated: false}));
            
            errorResult = {
              status: 401, 
              data: err.response.data,
              headers: err.response.headers,
              succeeded: false, 
              responseReceived: true,
              unAuthenticated: true,
              unAuthorized: false,
              canceled: false,
              noConnection: false, 
              timedOut: false, 
              networkError: false,
              unhandled: false
            };
        }
        else
          errorResult = {
            //non 401 nor 403 negative response returned by our server
            status: err.response.status, 
            data: err.response.data,
            headers: err.response.headers,
            succeeded: false, 
            responseReceived: true,
            unAuthenticated: false,
            unAuthorized: false,
            canceled: false,
            noConnection: false, 
            timedOut: false, 
            networkError: false,
            unhandled: false
          };
      }
      else if (err.code == "ERR_NETWORK")
      //we have response and status=0
        errorResult = {
          status: 0,
          succeeded: false, 
          responseReceived: true,
          unAuthenticated: false,
          unAuthorized: false,
          canceled: false,
          noConnection: false, 
          timedOut: false,
          networkError: true, 
          unhandled: false
        };
      else
        errorResult = {
          succeeded: false, 
          responseReceived: true,
          unAuthenticated: false,
          unAuthorized: false,
          canceled: false,
          noConnection: false, 
          timedOut: false,
          networkError: false, 
          unhandled: true
        };
    }
    else if (err.request) 
    {
      if (err.code == "ECONNABORTED")
        //i will handle timeout problem here and not in pros.request because timeout might have been caught by a previous request layer
        //such as waitndRetry in which case pros.request doesn't run and we move directly to pros.response
        errorResult = {
          succeeded: false, 
          responseReceived: false,
          unAuthenticated: false,
          unAuthorized: false,
          canceled: false,
          noConnection: false, 
          timedOut: true,
          networkError: false,
          unhandled: false           
        };
      else
        errorResult = {
          succeeded: false, 
          responseReceived: false,
          unAuthenticated: false,
          unAuthorized: false,
          canceled: false,
          noConnection: false, 
          timedOut: false, 
          networkError: false,
          unhandled: true
        };
    } 
    else if (err.__transformResponse__noConnection || !navigator.onLine)
    {
      //being here => pros.request has directed me here and thus we have err doesn't contain request as it only contains __transformResponse__noConnection
      //so the above elseif is false when no connection and i don't get into it no connection
      //cleaning
      if (err.__transformResponse__noConnection)
          delete err.__transformResponse__noConnection;

      errorResult = {
        succeeded: false, 
        responseReceived: false,
        unAuthenticated: false,
        unAuthorized: false,
        canceled: false,
        noConnection: true, 
        timedOut: false,
        networkError: false,
        unhandled: false
      };
    }   
    else
      errorResult = {
        succeeded: false, 
        responseReceived: false,
        unAuthenticated: false,
        unAuthorized: false,
        canceled: false,
        noConnection: false, 
        timedOut: false, 
        networkError: false,
        unhandled: true
      };

    return Promise.reject(errorResult); //or throw errorResult
  }
);


export const api = async ({ url, method = "GET", data = undefined, headers = {}, timeout = undefined, addSignalRConnectionIdToHeaders = true}) => {
  let instanceHeaders = {};
  const state = store?.getState();
  const dispatch = store?.dispatch;

  if (axiosInstance && axiosInstance.defaults && axiosInstance.defaults.headers)
      instanceHeaders = { ...axiosInstance.defaults.headers };

  if (!headers)
      headers = {};


  //add signalR connection id to the request if needed
  if (addSignalRConnectionIdToHeaders)
  {
      let signalRConnectionId = "";
      if (state && state.signalR && state.signalR.connectionId)
        headers[SIGNALR_CONNECTIONID_HEADER_NAME] = state.signalR.connectionId;
  }

  
  return axiosInstance({
      //if a default instance prop value is specified (ex. timeout: 30000) and we set that property below to undefined (ex. timeout: undefined)
      //in the request, timeout will be be 30000 not undefined; if below timeout is 10000 (i.e different than undefined) => it overrides the default
      url,
      method,
      data,
      headers: { ...instanceHeaders, ...headers},
      timeout, //if not passed => use that of the instance.defaults
  })
    .catch(err => {
         console.log("error inside api().catch; error > ");
         console.log(err);
         return Promise.reject(err);
    });
};


//api errors handler with default implementations
export const apiErrorsHandler = (state, dispatch, navigate, payload, redirectTo, handlers) =>{
  if (!payload || !handlers)
      handlers = {};
  
  if (payload.noConnection)
  {
    if (!handlers.noConnection)
    {
      dispatch(addNotification({text: "You are not connected to the Internet!", type:"error", closable: true, duration: 3, position: "topRight"}));
      if (redirectTo && navigate)
        navigate(redirectTo, {replace: true});
    }
    else      
        handlers.noConnection(state, dispatch, navigate, payload);        
  }
  else if (payload.networkError)
  {
    if (!handlers.networkError)
    {
      dispatch(addNotification({text: "A network error was detected! Maybe the server is not available.", type:"error", closable: true, duration: 3, position: "topRight"}));
      if (redirectTo && navigate)
        navigate(redirectTo, {replace: true});
    }
    else    
      handlers.networkError(state, dispatch, navigate, payload);
  }
  else if (payload.timedOut)
  {
    if (!handlers.timedOut)
    {
      dispatch(addNotification({text: "The server seems busy! The connection has timed out.", type:"error", closable: true, duration: 3, position: "topRight"}));
      if (redirectTo && navigate)
        navigate(redirectTo, {replace: true});
    }
    else    
      handlers.timedOut(state, dispatch, navigate, payload);
  }
  else if (payload.canceled)
  {
    if (!handlers.canceled)
    {
      dispatch(addNotification({text: "The operation was canceled!", type:"error", closable: true, duration: 3, position: "topRight"}));
      if (redirectTo && navigate)
        navigate(redirectTo, {replace: true});
    }
    else
      handlers.canceled(state, dispatch, navigate, payload);    
  }
  else if (payload.unAuthenticated)
  {
    if (!handlers.unAuthenticated)
    {
      dispatch(addNotification({text: "You are not logged to the system!", type:"error", closable: true, duration: 3, position: "topRight"}));
      if (navigate)
        navigate("/login", {replace: true});
    }
    else
      handlers.unAuthenticated(state, dispatch, navigate, payload);
  }
  else if (payload.unAuthorized)
  {
    if (!handlers.unAuthorized)
    {
      dispatch(setGenericErrorPage({title: "UnAuthorized!", body: "You are not authorized to access the specified resource."}));
      
      if (navigate && navigate)
        navigate("/unauthorized", {replace: true});
    }
    else
      handlers.unAuthorized(state, dispatch, navigate, payload);
  }
  else if (payload.responseReceived)
  {
    //my server returned error which could be 200, xxx, or 500
    if (handlers.responseReceived)
        handlers.responseReceived(state, dispatch, navigate, payload);
    else
    {
      let errorMessages = payload.data.ErrorMessages;
      if (!errorMessages || errorMessages.length == 0)
          dispatch(addNotification({text: "An unknown error occured!", type: "error", closable: true, duration:3, position:"topRight"}));
      else
      {
          errorMessages = getErrorMessages(errorMessages);

          const items = errorMessages.map((e, index) => (<li key={index}>{e}</li>));
          dispatch(setAppDialog({isVisible: true, content: <ul>{items}</ul>, actions:{}}));
      }

      if (redirectTo && navigate)
        navigate(redirectTo, {replace: true});
    }
  }
  else if (payload.unhandled)
  {
    //response IS NOT received for sure
    if (!handlers.unhandled)
      {
        dispatch(addNotification({text: "An unknown error occured!", type:"error", closable: true, duration: 3, position: "topRight"}));
        if (redirectTo && navigate)
          navigate(redirectTo, {replace: true});
    }
    else    
      handlers.unhandled(state, dispatch, navigate, payload);      
  }
  else
  {
    //will never happen
    dispatch(setGenericErrorPage({title: "Error!", body: "An unknown error occured"}));

    if (navigate)
      navigate("/error");
  }
};


export const getErrorMessages = (errorMesssages, maxErrors=-1) => {
  //returns an array of string=error messages from an error messages object with the ability to specify the max
  //number of returned errors
  let result = [];
  let index = 0;

  if (!errorMesssages)
     return result;

  Object.keys(errorMesssages).forEach(function (key) {
    //push the first error of each prop
      result.push(errorMesssages[key][0]);

      ++index;

      if (maxErrors > 0 && index >= maxErrors)
         return result;
  });
  
  return result;
};
